import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from 'react';

export interface Position { left: number; top: number }

const MARGE = 8;
const HAUT_BARRE = 56;

/** Hauteur du bandeau « Environnement DEV » fixé en bas de l'écran (0 s'il n'y en a pas) : la fenêtre ne doit pas passer dessous. */
function hauteurBandeau(): number {
  const racine = document.documentElement;
  if (!racine.classList.contains('has-dev-env-banner')) return 0;
  return parseFloat(getComputedStyle(racine).getPropertyValue('--dev-env-banner-h')) || 0;
}

/**
 * Place une fenêtre à côté de l'élément qui l'a ouverte, sans JAMAIS le recouvrir : à droite s'il y a de la place, sinon à gauche, sinon au-dessus ou
 * au-dessous. Alignée verticalement sur lui, et toujours entièrement dans l'écran. Se replace quand la fenêtre change de taille, que la page défile
 * ou que la fenêtre du navigateur est redimensionnée. `selecteur` retrouve l'élément à chaque calcul (il peut avoir été redessiné).
 */
export function useAncrage(selecteur: string, ref: RefObject<HTMLElement | null>): Position | null {
  const [pos, setPos] = useState<Position | null>(null);

  const placer = useCallback(() => {
    const fen = ref.current;
    const ancre = document.querySelector<HTMLElement>(selecteur);
    if (!fen || !ancre) return;
    const r = ancre.getBoundingClientRect();
    const w = fen.offsetWidth, h = fen.offsetHeight, vw = window.innerWidth, vh = window.innerHeight - hauteurBandeau();
    const haut = Math.min(Math.max(HAUT_BARRE, r.top - 40), Math.max(HAUT_BARRE, vh - h - MARGE));
    let left: number, top = haut;
    if (r.right + MARGE + w <= vw - MARGE) left = r.right + MARGE;                 // à droite de la séance
    else if (r.left - MARGE - w >= MARGE) left = r.left - MARGE - w;              // sinon à gauche
    else {                                                                          // écran étroit : au-dessous ou au-dessus
      left = Math.min(Math.max(MARGE, r.left), vw - w - MARGE);
      top = r.bottom + 4 + h <= vh - MARGE ? r.bottom + 4 : Math.max(HAUT_BARRE, r.top - h - 4);
    }
    setPos((p) => (p && p.left === Math.round(left) && p.top === Math.round(top) ? p : { left: Math.round(left), top: Math.round(top) }));
  }, [selecteur, ref]);

  useLayoutEffect(placer);   // à chaque rendu : le contenu a pu changer la hauteur de la fenêtre
  useEffect(() => {
    window.addEventListener('resize', placer);
    window.addEventListener('scroll', placer, true);
    const ro = new ResizeObserver(placer);
    if (ref.current) ro.observe(ref.current);
    return () => { window.removeEventListener('resize', placer); window.removeEventListener('scroll', placer, true); ro.disconnect(); };
  }, [placer, ref]);

  return pos;
}
